import React, { useEffect, useState } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Avatar
} from '@mui/material';
import {
    DirectionsCar,
    ReportProblem,
    LocalGasStation,
    Speed,
    Business,
    VpnKey,
    Assignment
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { fuelApi, FuelTransaction } from '../services/fuelApi';
import { mileageApi, MileageLog } from '../services/mileageApi';
import { maintenanceApi, Maintenance } from '../services/maintenanceApi';
import { repairsApi, Repair } from '../services/repairsApi';
import { documentsApi, Document } from '../services/documentsApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Line,
    ComposedChart,
    Legend
} from 'recharts';

const PIE_COLORS: Record<string, string> = {
    'Carburant': '#f5a623',
    'Vidange': '#0d9488',
    'Pneus': '#3b82f6',
    'Révision': '#8b5cf6',
    'Freins': '#e53e3e',
    'Batterie': '#f59e0b',
    'Courroie': '#10b981',
    'Injection': '#6366f1',
    'Autre': '#94a3b8'
};

const Dashboard: React.FC = () => {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(true);

    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [fuelList, setFuelList] = useState<FuelTransaction[]>([]);
    const [maintList, setMaintList] = useState<Maintenance[]>([]);
    const [repairList, setRepairList] = useState<Repair[]>([]);
    const [mileageList, setMileageList] = useState<MileageLog[]>([]);
    const [docsList, setDocsList] = useState<Document[]>([]);

    useEffect(() => {
        const loadAllData = async () => {
            setLoading(true);
            try {
                const [
                    vehRes,
                    fuelRes,
                    maintRes,
                    repairRes,
                    mileageRes,
                    docsRes
                ] = await Promise.all([
                    vehiclesApi.getAll(),
                    fuelApi.getAll(),
                    maintenanceApi.getAll(),
                    repairsApi.getRepairs().catch(() => []),
                    mileageApi.getAll(),
                    documentsApi.getDocuments().catch(() => [])
                ]);

                if (vehRes.success) setVehicles(vehRes.data);
                if (fuelRes.success) setFuelList(fuelRes.data);
                if (maintRes.success) setMaintList(maintRes.data);
                setRepairList(repairRes);
                if (mileageRes.success) setMileageList(mileageRes.data);
                setDocsList(docsRes);
            } catch (error) {
                console.error("Failed to load dashboard metrics:", error);
            } finally {
                setLoading(false);
            }
        };
        loadAllData();
    }, []);

    const getLocalizedCategoryName = (cat: string) => {
        if (cat === 'Carburant') return t('menu.parc.fuel') || 'Carburant';
        if (cat === 'Pneus') return t('parc.maintenance.catTires') || 'Pneus';
        if (cat === 'Freins') return t('parc.maintenance.catBrakes') || 'Freins';
        if (cat === 'Autre') return t('parc.maintenance.catOther') || 'Autre';
        if (cat === 'Vidange') return t('parc.dashboard.catVidange') || 'Vidange';
        if (cat === 'Révision') return t('parc.dashboard.catRevision') || 'Révision';
        if (cat === 'Batterie') return t('parc.dashboard.catBattery') || 'Batterie';
        if (cat === 'Courroie') return t('parc.dashboard.catBelt') || 'Courroie';
        if (cat === 'Injection') return t('parc.dashboard.catInjection') || 'Injection';
        return cat;
    };

    if (loading) {
        return (
            <GenericPage title={t('parc.dashboard.title') || "Tableau de bord"} footerEnabled={false}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress size={50} />
                </Box>
            </GenericPage>
        );
    }

    // --- CALCULATIONS (FleetOS Metrics) ---
    const totalVehiclesCount = vehicles.length;
    const activeVehiclesCount = vehicles.filter(v => v.status === 'active' || v.status === 'Actif').length;

    // Costs
    const fuelCostTotal = fuelList.reduce((sum, f) => sum + (f.cost || 0), 0);
    const maintCostTotal = maintList.reduce((sum, m) => sum + (m.cost || 0), 0);
    const repairsCostTotal = repairList.reduce((sum, r) => sum + (r.rprCost || 0), 0);
    const totalFleetCost = fuelCostTotal + maintCostTotal + repairsCostTotal;

    // Fuel metrics
    const totalLitres = fuelList.reduce((sum, f) => sum + (f.liters || 0), 0);
    const avgPricePerLitre = totalLitres > 0 ? (fuelCostTotal / totalLitres) : 31;

    // Mileage & distance
    const totalDistance = mileageList.reduce((sum, k) => sum + (k.distance || 0), 0);
    const avgCostPerKm = totalDistance > 0 ? (totalFleetCost / totalDistance) : (totalFleetCost / (vehicles.reduce((s, v) => s + (v.mileage || 0), 0) || 1));

    // Calculate vehicle statistics for groupings
    const getVehicleCosts = (vehId: number) => {
        const fuel = fuelList.filter(f => f.vehicleId === vehId).reduce((s, f) => s + (f.cost || 0), 0);
        const maint = maintList.filter(m => m.vehicleId === vehId).reduce((s, m) => s + (m.cost || 0), 0);
        const rprs = repairList.filter(r => r.vehIdf === vehId).reduce((s, r) => s + (r.rprCost || 0), 0);
        return fuel + maint + rprs;
    };

    // Proprietors grouping
    const oxxoVehicles = vehicles.filter(v => v.proprietor === 'OXXO' || v.proprietor === 'OXXO Algérie Spa' || (v.proprietor && v.proprietor.toUpperCase().includes('OXXO')));
    const cevitalVehicles = vehicles.filter(v => v.proprietor === 'Location Cevital MTP' || v.proprietor === 'CEVITAL' || (v.proprietor && v.proprietor.toUpperCase().includes('CEVITAL')));
    const otherVehicles = vehicles.filter(v => !oxxoVehicles.includes(v) && !cevitalVehicles.includes(v));

    const oxxoCost = oxxoVehicles.reduce((sum, v) => sum + getVehicleCosts(v.id), 0);
    const cevitalCost = cevitalVehicles.reduce((sum, v) => sum + getVehicleCosts(v.id), 0);
    const otherCost = otherVehicles.reduce((sum, v) => sum + getVehicleCosts(v.id), 0);

    // Alerts
    const overconsumptionLimit = 10;
    const today = new Date();

    // Compute overconsuming vehicles count
    const overconsumingVehs = vehicles.filter(v => {
        const vFuel = fuelList.filter(f => f.vehicleId === v.id);
        const vDistance = mileageList.filter(k => k.vehicleId === v.id).reduce((s, k) => s + (k.distance || 0), 0);
        const vLitres = vFuel.reduce((s, f) => s + (f.liters || 0), 0);
        if (vDistance > 10 && vLitres > 0) {
            const conso = (vLitres / vDistance) * 100;
            return conso > overconsumptionLimit;
        }
        return false;
    }).length;

    // Document alerts (expired or expiring in next 30 days)
    const documentAlerts = docsList.filter(d => {
        if (!d.expiryDate) return false;
        const diffTime = new Date(d.expiryDate).getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 30;
    }).length;

    // Maintenance pending alerts
    const maintAlerts = maintList.filter(m => m.status === 'planned' || m.status === 'pending' || m.status === 'scheduled').length;
    const totalAlertsCount = overconsumingVehs + documentAlerts + maintAlerts;

    // --- RECHARTS CHART DATA ---

    // 1. Monthly costs stacked bar chart
    const last6Months = Array.from({ length: 6 }).map((_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const locale = language === 'ar' ? 'ar-DZ' : language === 'en' ? 'en-US' : 'fr-FR';
        return {
            key: `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`,
            label: d.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
        };
    }).reverse();

    const monthlyData = last6Months.map(m => {
        // filter fuel transactions for this month
        const fuelMonth = fuelList
            .filter(f => {
                if (!f.date) return false;
                const d = new Date(f.date);
                const monthKey = `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                return monthKey === m.key;
            })
            .reduce((sum, f) => sum + (f.cost || 0), 0);

        // filter maintenance and repairs for this month
        const maintMonth = maintList
            .filter(ml => {
                if (!ml.startDate && !ml.scheduledDate) return false;
                const d = new Date(ml.startDate || ml.scheduledDate!);
                const monthKey = `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                return monthKey === m.key;
            })
            .reduce((sum, ml) => sum + (ml.cost || 0), 0);

        const repairMonth = repairList
            .filter(r => {
                if (!r.rprDate) return false;
                const d = new Date(r.rprDate);
                const monthKey = `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                return monthKey === m.key;
            })
            .reduce((sum, r) => sum + (r.rprCost || 0), 0);

        return {
            name: m.label,
            Carburant: Math.round(fuelMonth),
            Maintenance: Math.round(maintMonth + repairMonth)
        };
    });

    // 2. Répartition des dépenses (Doughnut Chart)
    const breakdownCategories: Record<string, number> = {
        'Carburant': fuelCostTotal,
        'Vidange': 0,
        'Pneus': 0,
        'Révision': 0,
        'Freins': 0,
        'Batterie': 0,
        'Courroie': 0,
        'Injection': 0,
        'Autre': 0
    };

    maintList.forEach(m => {
        const title = (m.title || '').toLowerCase();
        let matched = false;
        for (const cat of Object.keys(breakdownCategories)) {
            if (cat !== 'Carburant' && cat !== 'Autre' && title.includes(cat.toLowerCase())) {
                breakdownCategories[cat] += m.cost || 0;
                matched = true;
                break;
            }
        }
        if (!matched) {
            breakdownCategories['Autre'] += m.cost || 0;
        }
    });

    repairList.forEach(r => {
        const desc = (r.rprDescription || '').toLowerCase();
        let matched = false;
        for (const cat of Object.keys(breakdownCategories)) {
            if (cat !== 'Carburant' && cat !== 'Autre' && desc.includes(cat.toLowerCase())) {
                breakdownCategories[cat] += r.rprCost || 0;
                matched = true;
                break;
            }
        }
        if (!matched) {
            breakdownCategories['Autre'] += r.rprCost || 0;
        }
    });

    const pieData = Object.entries(breakdownCategories)
        .map(([name, value]) => ({ 
            name: getLocalizedCategoryName(name), 
            value: Math.round(value), 
            color: PIE_COLORS[name] || '#94a3b8' 
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);

    // 3. Top 6 Véhicules les plus coûteux (Horizontal Bar Chart)
    const topVehicles = vehicles
        .map(v => ({
            immat: v.registrationNumber,
            totalCost: getVehicleCosts(v.id),
            model: `${v.brand} ${v.model}`
        }))
        .filter(x => x.totalCost > 0)
        .sort((a, b) => b.totalCost - a.totalCost)
        .slice(0, 6);

    const maxVehicleCost = topVehicles.length > 0 ? topVehicles[0].totalCost : 1;

    // Dynamic bar colors based on cost ratio
    const getBarColor = (val: number) => {
        const ratio = val / maxVehicleCost;
        if (ratio >= 0.8) return '#e53e3e';
        if (ratio >= 0.5) return '#f5a623';
        return '#0d9488';
    };

    const topVehData = topVehicles.map(tv => ({
        name: tv.immat,
        'Coût Total': tv.totalCost,
        fill: getBarColor(tv.totalCost),
        model: tv.model
    }));

    // 4. Consommation L/100km mixed bar + line chart
    const consoLimitReference = 8;
    const vehConsos = vehicles
        .map(v => {
            const vFuel = fuelList.filter(f => f.vehicleId === v.id);
            const vDistance = mileageList.filter(k => k.vehicleId === v.id).reduce((s, k) => s + (k.distance || 0), 0);
            const vLitres = vFuel.reduce((s, f) => s + (f.liters || 0), 0);

            let l100 = 0;
            if (vDistance > 10 && vLitres > 0) {
                l100 = (vLitres / vDistance) * 100;
            }
            return {
                immat: v.registrationNumber,
                l100: Number(l100.toFixed(1))
            };
        })
        .filter(x => x.l100 > 0)
        .slice(0, 10); // Display top 10 for readability

    const fleetAvgConso = vehConsos.length > 0 ? (vehConsos.reduce((sum, x) => sum + x.l100, 0) / vehConsos.length) : 0;

    const consoChartData = vehConsos.map(vc => ({
        name: vc.immat,
        'L/100km': vc.l100,
        'Moyenne Flotte': Number(fleetAvgConso.toFixed(1)),
        'Référence': consoLimitReference,
        fill: vc.l100 > 12 ? 'rgba(229,62,62,0.85)' : vc.l100 > 9 ? 'rgba(245,166,35,0.85)' : vc.l100 > consoLimitReference ? 'rgba(245,166,35,0.6)' : 'rgba(13,148,136,0.85)'
    }));

    return (
        <GenericPage title={t('parc.dashboard.title') || "Tableau de Bord"} footerEnabled={false}>

            {/* KPI Cards Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, #f5a623 0%, #d48300 100%)',
                        color: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 6px 20px rgba(245, 166, 35, 0.3)',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'translateY(-4px)' }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.85 }}>{t('parc.dashboard.totalFleetCost') || 'COÛT TOTAL FLOTTE'}</Typography>
                                <LocalGasStation sx={{ opacity: 0.9 }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontFamily: 'Inconsolata, monospace' }}>
                                {Math.round(totalFleetCost / 1000).toLocaleString()} K DA
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500 }}>
                                {t('parc.dashboard.fuelCostSummary', Math.round(fuelCostTotal / 1000), Math.round((maintCostTotal + repairsCostTotal) / 1000))}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, #0d9488 0%, #056a5f 100%)',
                        color: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 6px 20px rgba(13, 148, 136, 0.3)',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'translateY(-4px)' }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.85 }}>{t('parc.dashboard.activeFleet') || 'FLOTTE ACTIVE'}</Typography>
                                <DirectionsCar sx={{ opacity: 0.9 }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                                {t('parc.dashboard.activeVehiclesCount', activeVehiclesCount, totalVehiclesCount)}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500 }}>
                                {t('parc.dashboard.avgCostPerVehicle', totalVehiclesCount > 0 ? Math.round(totalFleetCost / totalVehiclesCount).toLocaleString() : 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                        color: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 6px 20px rgba(124, 58, 237, 0.3)',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'translateY(-4px)' }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.85 }}>{t('parc.analysis.kmsTraveled').toUpperCase()}</Typography>
                                <Speed sx={{ opacity: 0.9 }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontFamily: 'Inconsolata, monospace' }}>
                                {totalDistance.toLocaleString()} <span style={{ fontSize: 16, fontWeight: 500, opacity: 0.8 }}>km</span>
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500 }}>
                                {t('parc.dashboard.mileageSummary', avgCostPerKm.toFixed(1), avgPricePerLitre.toFixed(1))}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, #e53e3e 0%, #b91c1c 100%)',
                        color: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 6px 20px rgba(229, 62, 62, 0.3)',
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'translateY(-4px)' }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.85 }}>{t('parc.dashboard.activeAlerts').toUpperCase() || 'ALERTES ACTIVES'}</Typography>
                                <ReportProblem sx={{ opacity: 0.9 }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                                {totalAlertsCount} <span style={{ fontSize: 16, fontWeight: 500, opacity: 0.8 }}>{t('parc.dashboard.alerts').toLowerCase()}</span>
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500 }}>
                                {t('parc.dashboard.alertsSummary', documentAlerts, maintAlerts, overconsumingVehs)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Proprietaires summary cards */}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('parc.dashboard.distributionByProprietor') || 'Répartition par Propriétaire'}</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: '12px' }}>
                        <Avatar sx={{ bgcolor: 'rgba(11,20,38,0.1)', color: '#0b1426', mr: 2, width: 48, height: 48 }}>
                            <Business />
                        </Avatar>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('parc.dashboard.oxxoProperty') || 'PROPRIÉTÉ OXXO'}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('parc.dashboard.vehiclesCount', oxxoVehicles.length)}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.totalCostKDA', Math.round(oxxoCost / 1000).toLocaleString())}</Typography>
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: '12px' }}>
                        <Avatar sx={{ bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', mr: 2, width: 48, height: 48 }}>
                            <VpnKey />
                        </Avatar>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('parc.dashboard.cevitalRental') || 'LOCATION CEVITAL MTP'}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('parc.dashboard.vehiclesCount', cevitalVehicles.length)}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.totalCostKDA', Math.round(cevitalCost / 1000).toLocaleString())}</Typography>
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: '12px' }}>
                        <Avatar sx={{ bgcolor: 'rgba(148,163,184,0.1)', color: '#94a3b8', mr: 2, width: 48, height: 48 }}>
                            <Assignment />
                        </Avatar>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('parc.dashboard.otherOwner') || 'AUTRE PROPRIÉTAIRE'}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('parc.dashboard.vehiclesCount', otherVehicles.length)}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.totalCostKDA', Math.round(otherCost / 1000).toLocaleString())}</Typography>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section Row 1 */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, height: '100%' }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('parc.dashboard.monthlyCostsCumulated') || 'Coûts Mensuels cumulés'}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.monthlyCostsCumulatedDesc') || 'Carburant + Maintenance & Réparations des 6 derniers mois (DZD)'}</Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                                    <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}K`} tick={{ fontSize: 11, fill: '#64748b' }} />
                                    <Tooltip formatter={(value: any) => [`${Number(value || 0).toLocaleString()} DA`]} />
                                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                                    <Bar dataKey="Carburant" name={t('menu.parc.fuel') || 'Carburant'} stackId="costs" fill="#f5a623" />
                                    <Bar dataKey="Maintenance" name={t('parc.maintenance.title') || 'Maintenance'} stackId="costs" fill="#0d9488" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, height: '100%' }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('parc.dashboard.expenseBreakdown') || 'Répartition des Dépenses'}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.expenseBreakdownDesc') || 'Volume global cumulé par poste de dépense'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: '100%', height: 160, display: 'flex', justifyContent: 'center' }}>
                                <ResponsiveContainer width="100%" height={160}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={70}
                                            paddingAngle={3}
                                            dataKey="value"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value: any) => [`${Number(value || 0).toLocaleString()} DA`]} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>

                            {/* Scrollable custom legend */}
                            <Box sx={{ width: '100%', maxHeight: 180, overflowY: 'auto', px: 1 }}>
                                {pieData.map((item, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: item.color }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>{item.name}</Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                            {Math.round(item.value / 1000).toLocaleString()}K DA
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section Row 2 */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, height: '100%' }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('parc.dashboard.top6Vehicles') || 'Top 6 — Véhicules les plus coûteux'}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.top6VehiclesDesc') || 'Coût total cumulé (Carburant + Entretien) en DA'}</Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={topVehData} layout="vertical" margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f6" />
                                    <XAxis type="number" tickFormatter={(v) => `${Math.round(v / 1000)}K`} tick={{ fontSize: 11, fill: '#64748b' }} />
                                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b', fontWeight: 'bold' }} />
                                    <Tooltip formatter={(value: any, name: any, props: any) => [`${Number(value || 0).toLocaleString()} DA`, `${props.payload.model}`]} />
                                    <Bar dataKey="Coût Total" name={t('parc.maintenance.cost') || 'Coût Total'} radius={[0, 4, 4, 0]} barSize={16}>
                                        {topVehData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, height: '100%' }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('parc.dashboard.consumptionEfficiency') || 'Rendement Consommation L/100km'}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('parc.dashboard.consumptionEfficiencyDesc') || 'Consommation moyenne par véhicule comparée aux seuils critiques (litres/100km)'}</Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 260 }}>
                            {consoChartData.length > 0 ? (
                                <ResponsiveContainer>
                                    <ComposedChart data={consoChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                                        <XAxis dataKey="name" angle={-20} textAnchor="end" tick={{ fontSize: 9, fill: '#64748b' }} height={50} />
                                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} unit=" L" />
                                        <Tooltip formatter={(value: any, name: any) => [`${value} L/100km`, name]} />
                                        <Bar dataKey="L/100km" name={t('parc.vehicles.fuelType') || 'L/100km'} radius={[4, 4, 0, 0]} barSize={16}>
                                            {consoChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                        <Line type="monotone" dataKey="Moyenne Flotte" name={t('parc.analysis.avgConsumption') || 'Moyenne Flotte'} stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={false} />
                                        <Line type="monotone" dataKey="Référence" stroke="#22c55e" strokeWidth={1.5} strokeDasharray="3 3" dot={false} activeDot={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', opacity: 0.6, p: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('parc.dashboard.insufficientMileageData')}</Typography>
                                    <Typography variant="caption" color="text.secondary">{t('parc.dashboard.checkFuelMileageHistory')}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </GenericPage>
    );
};

export default Dashboard;

